import React from 'react';
import { Formatters } from '../utils';
import { FormattedMessage } from 'react-intl';

// global none value text for now (exporting for test)
export const NONE = 'None';
export const YES = 'Yes';
export const NO = 'No';
export const PRICING = 'price';
export const NET_PRICE = 'net_price';
// TODO: discount field names are still changable
export const discountFields = [
  {
    name: 'discount1',
    label: (
      <FormattedMessage
        id="form.discount1"
        description="Discount 1 label"
        defaultMessage="+ Discount 1"
      />
    )
  },
  {
    name: 'discount2',
    label: (
      <FormattedMessage
        id="form.discount2"
        description="Discount 2 label"
        defaultMessage="+ Discount 2"
      />
    )
  },
  {
    name: 'discount3',
    label: (
      <FormattedMessage
        id="form.discount3"
        description="Discount 3 label"
        defaultMessage="+ Discount 3"
      />
    )
  }
];
// all functions here can overwrite steps and snapshot
export let steps = ['brand']; // used to place all column titles in order
let snapshot = []; // take snapshot of objects on each selection, reuse when stepping backwards

/**
 *
 * @function setSteps
 * @param {Array} data
 * @override steps - TODO: rely on BE endpoint
 * currently group out all existing column names, mash them by looking up previsouly saved steps
 * prev { a: 'xx', c: 'xx'} -> ['a', 'c']
 * next { a: 'xx', b: 'xx' } -> ['a', 'b', 'c']
 */
export const setSteps = data => {
  let prev;
  steps = data.reduce((acc, curr) => {
    Object.keys(curr).forEach(key => {
      if (
        !acc.includes(key) &&
        key !== 'updated_at' &&
        key !== 'currency' &&
        key !== NET_PRICE
      ) {
        // if one previous attribute is already in acc,
        // insert the current key after the previous one
        const idx = acc.indexOf(prev);
        if (idx > 0) {
          acc.splice(idx + 1, 0, key);
          // otherwise just append to the end
        } else {
          acc.push(key);
        }
      }
      prev = key;
    });
    return acc;
  }, []);
};

/**
 *
 * @function setFieldsFiltered
 * @param {Object} - idx, focus, filtered, fields
 * @override snapshot
 * @returns {Object} - focus, selected(always null), fields, filtered
 */
export const setFieldsFiltered = ({ idx = 0, focus = 0, filtered, fields }) => {
  const { focusedFields } = getFields({ idx, filtered }) || {};
  const updates = {
    // when idx and focus is the same, do not add next field
    fields:
      focusedFields && idx !== focus
        ? [...fields, { ...focusedFields }]
        : fields,
    filtered
  };
  snapshot[focus] = { ...updates };
  return {
    focus,
    selected: null,
    ...updates
  };
};

/**
 *
 * @function convertOptionValue
 * @param {*} data could be string, null, bool
 * convert the value depending on the data type
 */
export const convertOptionValue = value => {
  switch (value) {
    case true:
      return YES;
    case false:
      return NO;
    case undefined:
    case null:
    case '':
      return NONE;
    default:
      return value;
  }
};

/**
 *
 * @function getData
 * @param {*} data
 */
export const getData = data =>
  (data || [])
    .map(one => {
      let obj = {};
      // when value is empty, force add `None` - not sure about `>` yet
      Object.keys(one).forEach(
        key =>
          (obj[Formatters.toUnderscore(key)] = convertOptionValue(one[key]))
      );
      return obj;
    })
    // filter out if pricing doesn't exist
    .filter(one => one[PRICING] && one[PRICING] !== NONE);

/**
 *
 * @function getOptions
 * @memberof setFieldsFiltered
 * @param {Object} - idx, filtered
 * @returns options for one field based on idx
 */
const getOptions = ({ idx, filtered }) => {
  const options = filtered
    .reduce((acc, curr) => {
      Object.keys(curr).forEach(key => {
        const value = curr[key];
        if (key === steps[idx] && !acc.includes(value)) {
          acc.push(value);
        }
      });
      return acc;
    }, [])
    // sorting only for numbers - maybe do something for `None`?
    .sort((a, b) => Number(a) - Number(b));

  return options.map(option => ({
    text: option,
    value: option
  }));
};

/**
 *
 * @function getFields
 * @memberof setFieldsFiltered
 * @param {Object} - idx, filtered
 * @returns {Object} - idx, name, label, options or call own (getFields)
 */
export const getFields = ({ idx, filtered }) => {
  const options = getOptions({ idx, filtered });

  // currently just skip if the option is only one, and that is > or None
  if (
    !options.length ||
    (options.length === 1 &&
      (options[0].value === '>' || options[0].value === NONE))
  ) {
    return idx < steps.length - 1
      ? getFields({ idx: Number(idx) + 1, filtered })
      : null;
  } else {
    return {
      focusedFields: {
        idx,
        name: steps[idx],
        label: Formatters.toReadableStr(steps[idx]),
        options
      }
    };
  }
};

/**
 *
 * @function getUpdated
 * @param {Object} - value, name, changeValue (from changed)
 * @param {Object} - focus, filtered, fields, data (from state)
 * @returns {Object} - idx (of change field), newFields, focusedData
 */
export const getUpdated = ({
  value,
  name,
  changeValue,
  focus = 0,
  filtered,
  fields,
  data
}) => {
  const idx = steps.indexOf(name);
  let newFields = fields;
  let focusedData = filtered;
  let obj = {};

  // if backward, reset fields & filtred as reusing snapshots
  if (focus >= idx) {
    // reset data to the initial
    newFields = [fields[0]];
    focusedData = data;
    snapshot.forEach((shot, i) => {
      // shot might be already emptied out, then ignore
      if (!shot) {
        return;
      }

      // if the current changed field index is within the saved snapshot, keep it
      if (i < idx) {
        newFields = shot.fields;
        focusedData = shot.filtered;
        // otherwise delete it
      } else {
        snapshot[i] = null;
        // all other fields need to be specifically emptied out for formik
        shot &&
          shot.fields.forEach(field => {
            if (field.idx >= i) {
              obj[field.name] = '';
            }
          });
        // update the current changed one
        if (i === idx) {
          obj[name] = value;
        }
        changeValue(obj);
      }
    });
  }

  // filtered by the current selection
  focusedData = focusedData.filter(focused => {
    // because number values could be converted to string at Select dropdown field level
    // compare both as numbers
    return focused[name] === value || Number(focused[name]) === Number(value);
  });
  return {
    idx,
    newFields,
    focusedData,
    current: obj
  };
};
